import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ShieldAlert } from "lucide-react";

// Admin "Erase user (GDPR)" — destructive-action pattern: the admin must type
// the target's email exactly and give a reason (audit-logged). Server-side
// guards refuse admins, self and canonical demo accounts (approved 2026-02).
export function EraseUserDialog({ user, onErased }) {
  const [open, setOpen] = useState(false);
  const [typedEmail, setTypedEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const emailMatches = typedEmail.trim().toLowerCase() === (user.email || "").toLowerCase();
  const canErase = emailMatches && reason.trim().length >= 5 && !busy;

  const erase = async () => {
    if (!canErase) return;
    setBusy(true);
    try {
      await api.adminEraseUser(user.user_id, reason.trim());
      toast.success("User erased — sessions revoked, data deleted, shared records anonymised");
      setOpen(false);
      onErased(user.user_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erasure failed");
    } finally { setBusy(false); }
  };

  const reset = (o) => { setOpen(o); if (!o) { setTypedEmail(""); setReason(""); } };

  return (
    <>
      <button
        data-testid={`erase-user-${user.user_id}`}
        onClick={() => reset(true)}
        title={user.is_admin ? "Admins can't be erased — remove admin access first" : "Erase user (GDPR)"}
        disabled={user.is_admin}
        className="text-xs px-2.5 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1 transition-colors shrink-0 disabled:opacity-40 disabled:pointer-events-none"
      >
        <Trash2 className="w-3 h-3" /> Erase
      </button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent data-testid="erase-user-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="w-5 h-5" /> Erase user (GDPR)
            </DialogTitle>
            <DialogDescription>
              Permanently erases <span className="font-semibold">{user.org_name || user.name}</span>'s
              account and personal data. Sessions are revoked; hour logs and messages other
              people rely on are anonymised to "Deleted user". This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600">
                Type the user's email to confirm: <span className="font-mono text-red-600">{user.email}</span>
              </label>
              <Input
                data-testid="erase-confirm-email"
                value={typedEmail}
                onChange={(e) => setTypedEmail(e.target.value)}
                placeholder={user.email}
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600">Reason (audit-logged, required)</label>
              <Textarea
                data-testid="erase-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder='e.g. "GDPR erasure request via email ref #123"'
                rows={2}
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => reset(false)} className="rounded-full">Cancel</Button>
              <Button
                data-testid="erase-confirm-btn"
                onClick={erase}
                disabled={!canErase}
                className="rounded-full bg-red-600 hover:bg-red-700 text-white"
              >
                {busy ? "Erasing…" : "Erase permanently"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
